import React from 'react';
import { Section, TextField, TextAreaField, ImageField, VideoIdField, ListEditor } from './EditorFields';

// helper — updates a nested key like set(obj, 'hero.headingTop', 'Your')
const set = (obj, path, value) => {
  const parts = path.split('.');
  const clone = JSON.parse(JSON.stringify(obj));
  let cur = clone;
  for (let i = 0; i < parts.length - 1; i += 1) {
    if (cur[parts[i]] === undefined || cur[parts[i]] === null) cur[parts[i]] = {};
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = value;
  return clone;
};

const ContentEditor = ({ draft, setDraft }) => {
  const bind = (path) => (value) => setDraft((d) => set(d, path, value));
  const arr = (path, value) => setDraft((d) => set(d, path, value));

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-400">
        All changes are draft-only until you click <span className="text-white font-medium">Save changes</span> in the header.
        Media URLs can be pasted directly or picked from the Media Library.
      </p>

      {/* --- BRAND & NAV --- */}
      <Section id="brand" title="Brand & Navigation" subtitle="Logo, brand name, menu links" defaultOpen>
        <div className="grid md:grid-cols-2 gap-4">
          <TextField label="Brand name" value={draft.brand?.name} onChange={bind('brand.name')} />
          <TextField label="Tagline" value={draft.brand?.tagline} onChange={bind('brand.tagline')} />
        </div>
        <ImageField label="Logo image URL" value={draft.brand?.logoUrl} onChange={bind('brand.logoUrl')} />
        <TextField label="Website URL" value={draft.brand?.url} onChange={bind('brand.url')} />

        <div className="pt-2">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-2">Navigation links</div>
          <ListEditor
            items={draft.nav?.links || []}
            onChange={(v) => arr('nav.links', v)}
            addLabel="Add link"
            factory={() => ({ label: 'New link', href: '#' })}
            renderItem={(it, upd) => (
              <div className="grid grid-cols-2 gap-3">
                <TextField label="Label" value={it.label} onChange={(v) => upd({ ...it, label: v })} />
                <TextField label="Href" value={it.href} onChange={(v) => upd({ ...it, href: v })} />
              </div>
            )}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4 pt-2">
          <TextField label="Header CTA — label" value={draft.nav?.cta?.label} onChange={bind('nav.cta.label')} />
          <TextField label="Header CTA — href" value={draft.nav?.cta?.href} onChange={bind('nav.cta.href')} />
        </div>
      </Section>

      {/* --- HERO --- */}
      <Section id="hero" title="Hero" subtitle="First screen visitors see" defaultOpen>
        <TextField label="Eyebrow" value={draft.hero?.eyebrow} onChange={bind('hero.eyebrow')} />
        <div className="grid md:grid-cols-2 gap-4">
          <TextField label="Heading line 1 — plain" value={draft.hero?.headingTop} onChange={bind('hero.headingTop')} />
          <TextField label="Heading line 1 — highlighted" value={draft.hero?.headingHighlight} onChange={bind('hero.headingHighlight')} />
          <TextField label="Heading line 2 — plain" value={draft.hero?.headingMid} onChange={bind('hero.headingMid')} />
          <TextField label="Heading line 3 — accent" value={draft.hero?.headingAccent} onChange={bind('hero.headingAccent')} />
          <TextField label="Heading line 3 — end" value={draft.hero?.headingEnd} onChange={bind('hero.headingEnd')} />
        </div>
        <TextAreaField label="Body copy" value={draft.hero?.body} onChange={bind('hero.body')} rows={3} />
        <div className="grid md:grid-cols-2 gap-4">
          <TextField label="Primary CTA — label" value={draft.hero?.ctaPrimary?.label} onChange={bind('hero.ctaPrimary.label')} />
          <TextField label="Primary CTA — href" value={draft.hero?.ctaPrimary?.href} onChange={bind('hero.ctaPrimary.href')} />
          <TextField label="Secondary CTA — label" value={draft.hero?.ctaSecondary?.label} onChange={bind('hero.ctaSecondary.label')} />
          <TextField label="Secondary CTA — href" value={draft.hero?.ctaSecondary?.href} onChange={bind('hero.ctaSecondary.href')} />
        </div>
        <ImageField label="Background image" value={draft.hero?.backgroundImage} onChange={bind('hero.backgroundImage')} />

        <div className="pt-2 border-t border-white/5 mt-2">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 mt-3 mb-2">Featured slides (auto-rotating carousel)</div>
          <ListEditor
            items={draft.hero?.slides || []}
            onChange={(v) => arr('hero.slides', v)}
            addLabel="Add slide"
            factory={() => ({ badge: 'Featured', title: '', image: '', tag: '', chips: [] })}
            renderItem={(it, upd) => (
              <div className="space-y-3">
                <div className="grid md:grid-cols-2 gap-3">
                  <TextField label="Badge" value={it.badge} onChange={(v) => upd({ ...it, badge: v })} />
                  <TextField label="Title" value={it.title} onChange={(v) => upd({ ...it, title: v })} />
                  <TextField label="Tag (e.g. New)" value={it.tag} onChange={(v) => upd({ ...it, tag: v })} />
                </div>
                <ImageField label="Image" value={it.image} onChange={(v) => upd({ ...it, image: v })} />
                <TextField
                  label="Chips (comma-separated, up to 3)"
                  value={(it.chips || []).join(', ')}
                  onChange={(v) => upd({ ...it, chips: v.split(',').map((x) => x.trim()).filter(Boolean) })}
                />
              </div>
            )}
          />
        </div>

        <div className="pt-2 border-t border-white/5 mt-2">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 mt-3 mb-2">Fallback single featured card (used only if no slides)</div>
          <div className="grid md:grid-cols-2 gap-4">
            <TextField label="Badge" value={draft.hero?.feature?.badge} onChange={bind('hero.feature.badge')} />
            <TextField label="Title" value={draft.hero?.feature?.title} onChange={bind('hero.feature.title')} />
            <TextField label="Tag (e.g. New)" value={draft.hero?.feature?.tag} onChange={bind('hero.feature.tag')} />
          </div>
          <ImageField label="Product image" value={draft.hero?.feature?.image} onChange={bind('hero.feature.image')} />
          <TextField
            label="Feature chips (comma-separated)"
            value={(draft.hero?.feature?.chips || []).join(', ')}
            onChange={(v) => bind('hero.feature.chips')(v.split(',').map((x) => x.trim()).filter(Boolean))}
          />
        </div>

        <TextField
          label="Stats strip (comma-separated)"
          value={(draft.hero?.stats || []).join(', ')}
          onChange={(v) => bind('hero.stats')(v.split(',').map((x) => x.trim()).filter(Boolean))}
        />
      </Section>

      {/* --- SHOWREEL --- */}
      <Section id="showreel" title="Showreel (video-first)" subtitle="Right after the hero">
        <TextField label="Eyebrow" value={draft.showreel?.eyebrow} onChange={bind('showreel.eyebrow')} />
        <TextField label="Heading" value={draft.showreel?.heading} onChange={bind('showreel.heading')} />
        <TextAreaField label="Body copy" value={draft.showreel?.body} onChange={bind('showreel.body')} rows={2} />

        <div className="pt-2 border-t border-white/5 mt-2">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 mt-3 mb-2">Hero video (large)</div>
          <VideoIdField label="YouTube video" value={draft.showreel?.hero?.videoId} onChange={bind('showreel.hero.videoId')} />
          <TextField label="Video title" value={draft.showreel?.hero?.title} onChange={bind('showreel.hero.title')} />
          <TextField label="Badge" value={draft.showreel?.hero?.badge} onChange={bind('showreel.hero.badge')} />
          <ImageField label="Poster image" value={draft.showreel?.hero?.poster} onChange={bind('showreel.hero.poster')} />
        </div>

        <div className="pt-2 border-t border-white/5 mt-2">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 mt-3 mb-2">Side thumbnails</div>
          <ListEditor
            items={draft.showreel?.thumbs || []}
            onChange={(v) => arr('showreel.thumbs', v)}
            addLabel="Add thumbnail"
            factory={() => ({ videoId: '', title: '', poster: '', badge: '' })}
            renderItem={(it, upd) => (
              <div className="space-y-3">
                <VideoIdField label="YouTube video" value={it.videoId} onChange={(v) => upd({ ...it, videoId: v })} />
                <TextField label="Title" value={it.title} onChange={(v) => upd({ ...it, title: v })} />
                <TextField label="Badge" value={it.badge} onChange={(v) => upd({ ...it, badge: v })} />
                <ImageField label="Poster image" value={it.poster} onChange={(v) => upd({ ...it, poster: v })} />
              </div>
            )}
          />
        </div>
      </Section>

      {/* --- BRAND LOGOS MARQUEE --- */}
      <Section id="brandLogos" title="Trusted Brands Marquee" subtitle="Logo strip that scrolls">
        <ListEditor
          items={draft.brandLogos || []}
          onChange={(v) => arr('brandLogos', v)}
          addLabel="Add brand logo"
          factory={() => ({ name: 'Brand', src: '' })}
          renderItem={(it, upd) => (
            <div className="space-y-3">
              <TextField label="Brand name" value={it.name} onChange={(v) => upd({ ...it, name: v })} />
              <ImageField label="Logo image" value={it.src} onChange={(v) => upd({ ...it, src: v })} />
            </div>
          )}
        />
      </Section>

      {/* --- ABOUT --- */}
      <Section id="about" title="About" subtitle="Company story">
        <TextField label="Eyebrow" value={draft.about?.eyebrow} onChange={bind('about.eyebrow')} />
        <TextField label="Heading" value={draft.about?.heading} onChange={bind('about.heading')} />
        <TextAreaField label="Paragraph 1" value={draft.about?.paragraphs?.[0]} onChange={(v) => setDraft((d) => set(d, 'about.paragraphs.0', v))} rows={4} />
        <TextAreaField label="Paragraph 2" value={draft.about?.paragraphs?.[1]} onChange={(v) => setDraft((d) => set(d, 'about.paragraphs.1', v))} rows={4} />
        <ImageField label="Image" value={draft.about?.image} onChange={bind('about.image')} />
        <div className="grid md:grid-cols-2 gap-4">
          <TextField label="Mission eyebrow" value={draft.about?.missionEyebrow} onChange={bind('about.missionEyebrow')} />
          <TextField label="Mission text" value={draft.about?.missionText} onChange={bind('about.missionText')} />
        </div>
      </Section>

      {/* --- VALUES --- */}
      <Section id="values" title="Values" subtitle="4 pillars">
        <ListEditor
          items={draft.values || []}
          onChange={(v) => arr('values', v)}
          addLabel="Add value"
          factory={() => ({ title: '', desc: '', icon: 'Sparkles' })}
          renderItem={(it, upd) => (
            <div className="grid md:grid-cols-3 gap-3">
              <TextField label="Title" value={it.title} onChange={(v) => upd({ ...it, title: v })} />
              <TextAreaField label="Description" value={it.desc} onChange={(v) => upd({ ...it, desc: v })} className="md:col-span-2" rows={2} />
              <TextField label="Icon (Lucide name)" value={it.icon} onChange={(v) => upd({ ...it, icon: v })} className="md:col-span-3" />
            </div>
          )}
        />
      </Section>

      {/* --- SOLUTIONS --- */}
      <Section id="solutions" title="Solutions" subtitle="Solution/product cards">
        <ListEditor
          items={draft.solutions || []}
          onChange={(v) => arr('solutions', v)}
          addLabel="Add solution"
          factory={() => ({
            id: 'new-solution',
            title: '',
            tagline: '',
            partners: [],
            image: '',
            icon: 'Monitor',
            videoId: '',
            href: '#new-solution',
          })}
          renderItem={(it, upd) => (
            <div className="space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <TextField label="ID (anchor)" value={it.id} onChange={(v) => upd({ ...it, id: v })} />
                <TextField label="Icon (Lucide name)" value={it.icon} onChange={(v) => upd({ ...it, icon: v })} />
              </div>
              <TextField label="Title" value={it.title} onChange={(v) => upd({ ...it, title: v })} />
              <TextField label="Tagline" value={it.tagline} onChange={(v) => upd({ ...it, tagline: v })} />
              <TextField
                label="Partners (comma-separated)"
                value={(it.partners || []).join(', ')}
                onChange={(v) => upd({ ...it, partners: v.split(',').map((x) => x.trim()).filter(Boolean) })}
              />
              <ImageField label="Image" value={it.image} onChange={(v) => upd({ ...it, image: v })} />
              <VideoIdField label="Video (optional)" value={it.videoId} onChange={(v) => upd({ ...it, videoId: v })} />
              <TextField label="Learn more link (href)" value={it.href} onChange={(v) => upd({ ...it, href: v })} />
            </div>
          )}
        />
      </Section>

      {/* --- INDUSTRIES --- */}
      <Section id="industries" title="Industries" subtitle="Sectors served">
        <ListEditor
          items={draft.industries || []}
          onChange={(v) => arr('industries', v)}
          addLabel="Add industry"
          factory={() => ({ name: '', desc: '', icon: 'ShoppingBag' })}
          renderItem={(it, upd) => (
            <div className="space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <TextField label="Name" value={it.name} onChange={(v) => upd({ ...it, name: v })} />
                <TextField label="Icon (Lucide name)" value={it.icon} onChange={(v) => upd({ ...it, icon: v })} />
              </div>
              <TextAreaField label="Description" value={it.desc} onChange={(v) => upd({ ...it, desc: v })} rows={2} />
            </div>
          )}
        />
      </Section>

      {/* --- FEATURED BRANDS --- */}
      <Section id="featuredBrands" title="Featured Brands" subtitle="Deep-dive brand cards with video">
        <ListEditor
          items={draft.featuredBrands || []}
          onChange={(v) => arr('featuredBrands', v)}
          addLabel="Add featured brand"
          factory={() => ({ name: '', tagline: '', logo: '', videoId: '', videoTitle: '', images: [''], website: '' })}
          renderItem={(it, upd) => (
            <div className="space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <TextField label="Name" value={it.name} onChange={(v) => upd({ ...it, name: v })} />
                <TextField label="Website URL" value={it.website} onChange={(v) => upd({ ...it, website: v })} />
              </div>
              <TextField label="Tagline" value={it.tagline} onChange={(v) => upd({ ...it, tagline: v })} />
              <ImageField label="Brand logo" value={it.logo} onChange={(v) => upd({ ...it, logo: v })} />
              <ImageField label="Hero image" value={(it.images || [])[0]} onChange={(v) => upd({ ...it, images: [v] })} />
              <VideoIdField label="YouTube video" value={it.videoId} onChange={(v) => upd({ ...it, videoId: v })} />
              <TextField label="Video title" value={it.videoTitle} onChange={(v) => upd({ ...it, videoTitle: v })} />
              <TextField
                label="Features (comma-separated, LS Retail-style)"
                value={(it.features || []).join(', ')}
                onChange={(v) => {
                  const list = v.split(',').map((x) => x.trim()).filter(Boolean);
                  upd({ ...it, features: list.length ? list : undefined });
                }}
              />
            </div>
          )}
        />
      </Section>

      {/* --- CASE STUDIES --- */}
      <Section id="caseStudies" title="Case Studies" subtitle="Project outcomes">
        <ListEditor
          items={draft.caseStudies || []}
          onChange={(v) => arr('caseStudies', v)}
          addLabel="Add case study"
          factory={() => ({ title: '', industry: '', outcome: '', description: '', tags: [], href: '#' })}
          renderItem={(it, upd) => (
            <div className="space-y-3">
              <TextField label="Title" value={it.title} onChange={(v) => upd({ ...it, title: v })} />
              <div className="grid md:grid-cols-2 gap-3">
                <TextField label="Industry" value={it.industry} onChange={(v) => upd({ ...it, industry: v })} />
                <TextField label="Outcome / KPI" value={it.outcome} onChange={(v) => upd({ ...it, outcome: v })} />
              </div>
              <TextAreaField label="Description" value={it.description} onChange={(v) => upd({ ...it, description: v })} rows={3} />
              <TextField
                label="Tags (comma-separated)"
                value={(it.tags || []).join(', ')}
                onChange={(v) => upd({ ...it, tags: v.split(',').map((x) => x.trim()).filter(Boolean) })}
              />
              <TextField label="Link (href)" value={it.href} onChange={(v) => upd({ ...it, href: v })} />
            </div>
          )}
        />
      </Section>

      {/* --- STATS --- */}
      <Section id="stats" title="Stats strip">
        <ListEditor
          items={draft.stats || []}
          onChange={(v) => arr('stats', v)}
          addLabel="Add stat"
          factory={() => ({ value: '', label: '' })}
          renderItem={(it, upd) => (
            <div className="grid md:grid-cols-2 gap-3">
              <TextField label="Value" value={it.value} onChange={(v) => upd({ ...it, value: v })} />
              <TextField label="Label" value={it.label} onChange={(v) => upd({ ...it, label: v })} />
            </div>
          )}
        />
      </Section>

      {/* --- TESTIMONIALS --- */}
      <Section id="testimonials" title="Testimonials">
        <ListEditor
          items={draft.testimonials || []}
          onChange={(v) => arr('testimonials', v)}
          addLabel="Add testimonial"
          factory={() => ({ name: '', role: '', quote: '' })}
          renderItem={(it, upd) => (
            <div className="space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <TextField label="Name" value={it.name} onChange={(v) => upd({ ...it, name: v })} />
                <TextField label="Role" value={it.role} onChange={(v) => upd({ ...it, role: v })} />
              </div>
              <TextAreaField label="Quote" value={it.quote} onChange={(v) => upd({ ...it, quote: v })} rows={3} />
            </div>
          )}
        />
      </Section>

      {/* --- CONTACT --- */}
      <Section id="contact" title="Contact section">
        <TextField label="Eyebrow" value={draft.contact?.eyebrow} onChange={bind('contact.eyebrow')} />
        <TextField label="Heading" value={draft.contact?.heading} onChange={bind('contact.heading')} />
        <TextAreaField label="Body" value={draft.contact?.body} onChange={bind('contact.body')} rows={2} />
        <div className="grid md:grid-cols-2 gap-4">
          <TextField label="Company name" value={draft.contact?.company} onChange={bind('contact.company')} />
          <TextField label="Address" value={draft.contact?.address} onChange={bind('contact.address')} />
          <TextField label="Phone" value={draft.contact?.phone} onChange={bind('contact.phone')} />
          <TextField label="Mobile" value={draft.contact?.mobile} onChange={bind('contact.mobile')} />
          <TextField label="Email" value={draft.contact?.email} onChange={bind('contact.email')} />
          <TextField label="Google Maps URL" value={draft.contact?.mapUrl} onChange={bind('contact.mapUrl')} />
        </div>
      </Section>

      {/* --- FOOTER --- */}
      <Section id="footer" title="Footer">
        <TextAreaField label="Tagline" value={draft.footer?.tagline} onChange={bind('footer.tagline')} rows={3} />
        <TextField label="Sub-line" value={draft.footer?.subline} onChange={bind('footer.subline')} />
      </Section>

      {/* --- WHATSAPP CHAT --- */}
      <Section id="whatsapp" title="WhatsApp Chat Widget" subtitle="Floating chat button + Contact-page button">
        <div className="rounded-xl bg-black/20 border border-white/8 p-3 text-xs text-slate-400 mb-2">
          Toggle the widget on/off, edit the greeting, business hours, agent name, and the WhatsApp number that receives chats.
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3 md:col-span-2">
            <input
              id="wa-enabled"
              type="checkbox"
              checked={draft.whatsapp?.enabled !== false}
              onChange={(e) => bind('whatsapp.enabled')(e.target.checked)}
              className="h-4 w-4 accent-brand-green"
            />
            <label htmlFor="wa-enabled" className="text-sm text-white">
              Show floating WhatsApp button on the public site
            </label>
          </div>
          <TextField
            label="Phone (international, digits only)"
            value={draft.whatsapp?.phone}
            onChange={(v) => bind('whatsapp.phone')(String(v || '').replace(/[^0-9]/g, ''))}
            placeholder="962797587282"
          />
          <TextField label="Display phone (with + and spaces)" value={draft.whatsapp?.displayPhone} onChange={bind('whatsapp.displayPhone')} />
          <TextField label="Agent name" value={draft.whatsapp?.agentName} onChange={bind('whatsapp.agentName')} />
          <TextField label="Agent role / subtitle" value={draft.whatsapp?.agentRole} onChange={bind('whatsapp.agentRole')} />
        </div>
        <ImageField label="Agent avatar (optional)" value={draft.whatsapp?.agentAvatar} onChange={bind('whatsapp.agentAvatar')} />
        <TextAreaField label="Greeting message (first bubble)" value={draft.whatsapp?.greeting} onChange={bind('whatsapp.greeting')} rows={3} />
        <div className="grid md:grid-cols-2 gap-4">
          <TextField label="CTA button label" value={draft.whatsapp?.ctaLabel} onChange={bind('whatsapp.ctaLabel')} />
          <TextField label="Input placeholder" value={draft.whatsapp?.inputPlaceholder} onChange={bind('whatsapp.inputPlaceholder')} />
          <TextField label="Business hours (leave empty to hide)" value={draft.whatsapp?.hours} onChange={bind('whatsapp.hours')} />
          <TextField label="Teaser bubble text" value={draft.whatsapp?.teaser} onChange={bind('whatsapp.teaser')} />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <TextField
            label="Teaser delay (seconds, 0 = disabled)"
            value={String(draft.whatsapp?.teaserDelay ?? 0)}
            onChange={(v) => bind('whatsapp.teaserDelay')(Number(String(v).replace(/[^0-9]/g, '')) || 0)}
          />
        </div>
      </Section>
    </div>
  );
};

export default ContentEditor;
